/**
 * General-purpose static helper methods for strings, numbers, dates, and password generation.
 */
export class Helper {

    /**
     * Checks whether a string is undefined, null, or empty.
     * @param string The string to check.
     * @returns True if the string is undefined, null, or an empty string; otherwise false.
     */
    public static isStringNullOrEmpty(string?: string): boolean {
        return (string === undefined || string === null || string === '');
    }

    /**
     * Checks whether an array is null, undefined, or has no elements.
     * @param array The array to check.
     * @returns True if the array is null/undefined or has zero length; otherwise false.
     */
    public static isArrayNullOrEmpty(array?: any[]): boolean {
        return array == null || array.length == 0;
    }

    /**
     * Checks whether a value is a non-null object.
     * @param value The value to check.
     * @returns True if the value is not null/undefined and its type is "object"; otherwise false.
     */
    public static isObject(value?: any): boolean {
        return value != null && typeof value == "object";
    }

    /**
     * Checks whether an object has no own enumerable keys.
     * @param obj The object to check.
     * @returns True if the object has no keys; otherwise false.
     */
    public static isObjectEmpty(obj: { [key: string]: any }): boolean {
        return Object.keys(obj).length == 0;
    }

    /**
     * Determines whether a value is numeric, whether it is provided as a number or a string.
     * @param numString The number or string to test.
     * @returns True if the value is a number, or a string that parses as a valid number; otherwise false.
     */
    public static isNumeric(numString: number | string): boolean {
        let numStringType = typeof numString;
        if (numStringType != "string") {
            return (numStringType === "number");
        }
        else {
            return !isNaN(<number>numString) && !isNaN(parseFloat(<string>numString));
        }
    }

    /**
     * Determines whether a number is positive, optionally treating zero as positive.
     * @param numberValue The number to test.
     * @param allowZero When true, zero is considered positive; defaults to false.
     * @returns True if the number is greater than zero, or is zero and allowZero is true; otherwise false.
     */
    public static isNumberPositive(numberValue: number, allowZero: boolean = false): boolean {
        return numberValue > 0 || (allowZero && numberValue == 0);
    }

    /**
     * Checks whether an item is null/undefined or equal to a given value.
     * @param item The item to check.
     * @param value The value to compare against.
     * @returns True if the item is null/undefined or loosely equal to value; otherwise false.
     */
    public static isNullOrValue<T>(item: T, value: T): boolean {
        return item == null || item == value;
    }

    /**
     * Returns a fallback value when the given value is null or undefined.
     * @param value The value to check.
     * @param defaultValue The value to return if value is null or undefined.
     * @returns value if it is not null/undefined; otherwise defaultValue.
     */
    public static defaultIfNull<T>(value: T, defaultValue: T): T {
        return value != null ? value : defaultValue;
    }

    /**
     * Builds a compact timestamp string from a date's year, month, day, hours, minutes, seconds, and milliseconds.
     * @param date The date to format.
     * @param separator Character(s) used to join the date/time segments; defaults to no separator if omitted.
     * @returns The joined timestamp string.
     */
    public static getTimestampFromDate(date: Date, separator: string = ""): string {
        let { year, month, day } = this.getParsedDate(date);
        let { hours, minutes, seconds, milliseconds } = this.getParsedTime(date);

        return [year, month, day, hours, minutes, seconds, milliseconds].join(separator);
    }

    /**
     * Formats a date as an ISO-like date-time string (YYYY-MM-DDTHH:mm:ss.mmm).
     * @param date The date to format.
     * @returns The formatted date-time string.
     */
    public static getDateTimeString(date: Date): string {
        let { year, month, day } = this.getParsedDate(date);
        let { hours, minutes, seconds, milliseconds } = this.getParsedTime(date);

        return [year, month, day].join("-") + "T" + [hours, minutes, seconds].join(":") + "." + milliseconds;
    }

    /**
     * Extracts and zero-pads the year, month, and day components of a date.
     * @param date The date to parse.
     * @returns An object with padded year (4 digits), month (2 digits), and day (2 digits) strings.
     */
    public static getParsedDate(date: Date) {
        let year = this.getPaddedNumberString(date.getFullYear(), 4);
        let month = this.getPaddedNumberString(date.getMonth() + 1);
        let day = this.getPaddedNumberString(date.getDate());

        return {
            year, month, day
        };
    }

    /**
     * Extracts and zero-pads the hours, minutes, seconds, and milliseconds components of a date.
     * @param date The date to parse.
     * @returns An object with padded hours, minutes, seconds (2 digits each), and milliseconds (3 digits) strings.
     */
    public static getParsedTime(date: Date) {
        let hours = this.getPaddedNumberString(date.getHours());
        let minutes = this.getPaddedNumberString(date.getMinutes());

        let seconds = this.getPaddedNumberString(date.getSeconds());
        let milliseconds = this.getPaddedNumberString(date.getMilliseconds(), 3);

        return {
            hours, minutes, seconds, milliseconds
        };
    }

    /**
     * Strips a single leading zero from a numeric string, if present.
     * @param number The numeric string to trim.
     * @returns The string with a single leading "0" removed, or the original string if it did not start with "0".
     */
    public static removeNumberPadding(number: string): string {
        var displayValue = number;
        if (number.startsWith("0")) {
            displayValue = number.substring(1);
        }

        return displayValue;
    }

    /**
     * Zero-pads a number to a fixed number of digits by treating it as a fractional value scaled by 10^numOfDigits.
     * @param num The number to pad (e.g. a date/time component such as hours or month).
     * @param numOfDigits Target number of digits in the result; defaults to 2.
     * @returns The zero-padded numeric string.
     */
    public static getPaddedNumberString(num: number, numOfDigits: number = 2): string {
        let paddedNumberString = "";

        let paddedNumber = num / Math.pow(10, numOfDigits);
        let paddedNumberSplit = paddedNumber.toString().split(".");
        if (paddedNumberSplit.length > 1) {
            paddedNumberString = paddedNumberSplit[1];

            let digitsRemaining = numOfDigits - paddedNumberString.length;
            if (digitsRemaining != 0) {
                paddedNumberString += this.getZeroPaddedString(digitsRemaining);
            }
        } else {
            paddedNumberString = this.getZeroPaddedString(numOfDigits);
        }

        return paddedNumberString;
    }

    /**
     * Returns a new date offset from the given date by the specified number of days, months, and/or years.
     * @param date The base date; not mutated.
     * @param data Object specifying how many days, months, and/or years to add; any omitted field is left unchanged.
     * @returns A new Date instance with the offsets applied.
     */
    public static addToDate(date: Date, data: { days?: number, months?: number, years?: number }): Date {
        var currentDate = new Date(date.valueOf());

        if (data.days != null) {
            currentDate.setDate(currentDate.getDate() + data.days);
        }

        if (data.months != null) {
            currentDate.setMonth(currentDate.getMonth() + data.months);
        }

        if (data.years != null) {
            currentDate.setFullYear(currentDate.getFullYear() + data.years);
        }

        return currentDate;
    };

    /**
     * Builds a string consisting of a fixed number of "0" characters.
     * @param numOfZeros The number of zero characters to generate.
     * @returns The zero-filled string.
     */
    public static getZeroPaddedString(numOfZeros: number): string {
        let zeroPaddedString = "";

        let zeroPaddingArray = new Array(numOfZeros);
        for (let i = 0; i < zeroPaddingArray.length; i++) {
            zeroPaddedString += "0";
        }

        return zeroPaddedString;
    }

    /**
     * Generates a random password of a given length from configurable character sets (lowercase, uppercase, numbers, symbols), optionally regenerating until each "guaranteed" set is represented. Not cryptographically secure.
     * @param length Total length of the generated password; defaults to 12.
     * @param options Object configuring which character sets to include and which must be guaranteed to appear at least once; defaults to lowercase, uppercase, and numbers included (none guaranteed) and symbols excluded.
     * @returns The generated password string.
     */
    public static generatePassword(length = 12, {
        lowercase = { include: true, guarantee: false },
        uppercase = { include: true, guarantee: false },
        numbers = { include: true, guarantee: false },
        symbols = { include: false, guarantee: false },
    } = {}) {
        const sets = {
            lowercase: "abcdefghijklmnopqrstuvwxyz",
            uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
            numbers: "0123456789",
            symbols: "!@#$%^&*()-_=+[]{};:,.?/\\|~",
        };

        // Build character pool
        let alphabet = "";
        for (const [key, { include }] of Object.entries({ lowercase, uppercase, numbers, symbols })) {
            if (include) alphabet += sets[key];
        }

        if (!alphabet) throw new Error("No character sets enabled.");

        // Build password body
        let password = "";
        for (let i = 0; i < length; i++) {
            password += alphabet[Math.floor(Math.random() * alphabet.length)];
        }

        // Apply guarantees
        let isValid = true;
        for (const [key, { include, guarantee }] of Object.entries({ lowercase, uppercase, numbers, symbols })) {
            if (include && guarantee && !password.split("").some(ch => sets[key].includes(ch))) {
                isValid = false;
                break;
            }
        }
        if (!isValid) {
            // regenerate if guarantees not met
            password = this.generatePassword(length, { lowercase, uppercase, numbers, symbols });
        }

        return password;
    }

}
